/**
 * The transaction list (P1-TR3).
 *
 * One row per entry, with the status visible without tapping. A partner
 * scanning this is usually answering one of two questions: "did my entry go
 * in?" and "what has the farm spent this week?" — so the amount and the status
 * badge carry the row, and everything else is secondary.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { formatMoney } from '../../lib/currency.js';
import { formatDate } from '../../lib/date.js';
import { useCategories, useTransactions, type Transaction } from './api.js';
import { StatusBadge } from './StatusBadge.js';
import { TransactionAdminActions } from './TransactionAdminActions.js';
import { TransactionDetail } from './TransactionDetail.js';

/** Types that took money out, for colouring the amount. */
const OUTFLOW = new Set(['expense', 'drawing', 'transfer_out', 'payable_payment', 'asset_purchase']);

/*
 * Voided entries are hidden from "all" so a withdrawn ৳40,000 is never read as
 * real money, but every partner can still see what was removed and why (BR-08).
 */
const FILTERS = ['all', 'pending', 'approved', 'voided'] as const;
type Filter = (typeof FILTERS)[number];

export function TransactionList(): JSX.Element {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');
  /** The entry open in the detail view, if any. */
  const [openId, setOpenId] = useState<number | null>(null);

  const { data: categories = [] } = useCategories();
  const { data, isLoading, isError, refetch } = useTransactions(filter === 'all' ? {} : { status: filter });

  const categoryName = (id: number | null): string => {
    if (id === null) return t('finance.uncategorised');
    for (const parent of categories) {
      if (parent.id === id) return parent.name;
      const child = parent.children?.find((c) => c.id === id);
      if (child) return child.name;
    }
    return t('finance.uncategorised');
  };

  const openTxn: Transaction | undefined = data?.items.find((txn) => txn.id === openId);

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('finance.list.title')}</h1>

      <div className="mb-4 flex gap-2" role="group" aria-label={t('finance.list.filter')}>
        {FILTERS.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setFilter(option)}
            aria-pressed={filter === option}
            className={`min-h-touch flex-1 rounded-lg px-3 py-2 text-sm ${
              filter === option ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
            }`}
          >
            {t(`finance.list.filter.${option}`)}
          </button>
        ))}
      </div>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {data?.items.length === 0 && <p className="py-8 text-center text-gray-500">{t('finance.list.empty')}</p>}

      {/* Phone: one card per entry. */}
      <ul className="space-y-2 md:hidden">
        {data?.items.map((txn) => (
          <li key={txn.id} className="rounded-xl bg-white shadow-sm">
            <button
              type="button"
              onClick={() => setOpenId(txn.id)}
              aria-label={`${t('finance.detail.open')}: ${categoryName(txn.categoryId)}`}
              className="block w-full px-4 py-3 text-left"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate font-medium text-gray-800">{categoryName(txn.categoryId)}</span>
                <span
                  className={`shrink-0 tabular-nums font-semibold ${
                    OUTFLOW.has(txn.type) ? 'text-gray-900' : 'text-green-700'
                  }`}
                >
                  {OUTFLOW.has(txn.type) ? '−' : '+'} {formatMoney(txn.amount, { symbol: false })}
                </span>
              </div>

              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                <span>{formatDate(txn.txnDate)}</span>
                <StatusBadge status={txn.status} />
                {/* P1-BR8 — flagged, never blocked: the farm ran on paper first. */}
                {txn.isBackdated && (
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-blue-900">{t('finance.backdated')}</span>
                )}
                {txn.receiptUrl !== null && <span aria-label={t('finance.hasReceipt')}>📎</span>}
              </div>

              {txn.note !== null && <p className="mt-1 truncate text-sm text-gray-600">{txn.note}</p>}
            </button>
          </li>
        ))}
      </ul>

      {/* Desktop: the ledger as a ledger. Date first, because that is the
          column a partner scans when checking a figure against a receipt. */}
      {data !== undefined && data.items.length > 0 && (
        <div className="hidden overflow-x-auto rounded-xl bg-white shadow-sm md:block">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-28">{t('finance.list.date')}</th>
                <th>{t('finance.list.category')}</th>
                <th>{t('finance.list.note')}</th>
                <th className="w-32">{t('finance.list.status')}</th>
                <th className="num w-36">{t('finance.list.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((txn) => (
                <tr key={txn.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setOpenId(txn.id)}>
                  <td className="whitespace-nowrap text-gray-500">{formatDate(txn.txnDate)}</td>
                  <td className="font-medium text-gray-900">
                    {/* The row is clickable; the button is what a keyboard reaches. */}
                    <button type="button" onClick={() => setOpenId(txn.id)} className="text-left hover:underline">
                      {categoryName(txn.categoryId)}
                    </button>
                  </td>
                  <td className="max-w-xs truncate text-gray-600">{txn.note ?? '—'}</td>
                  <td className="whitespace-nowrap">
                    <StatusBadge status={txn.status} />
                    {txn.isBackdated && (
                      <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-900">
                        {t('finance.backdated')}
                      </span>
                    )}
                    {txn.receiptUrl !== null && (
                      <span className="ml-1" aria-label={t('finance.hasReceipt')}>
                        📎
                      </span>
                    )}
                  </td>
                  <td className={`num font-semibold ${OUTFLOW.has(txn.type) ? 'text-gray-900' : 'text-green-700'}`}>
                    {OUTFLOW.has(txn.type) ? '−' : '+'} {formatMoney(txn.amount, { symbol: false })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openTxn !== undefined && (
        <TransactionDetail
          txn={openTxn}
          categoryName={categoryName(openTxn.categoryId)}
          onClose={() => setOpenId(null)}
          actions={<TransactionAdminActions txn={openTxn} />}
        />
      )}
    </div>
  );
}
